/* eslint-disable react-refresh/only-export-components */

import {
  useEffect,
  useMemo,
  useContext,
  createContext,
  useReducer,
  FC,
  PropsWithChildren,
  useState,
} from 'react';

import Cookies from 'js-cookie';
import { useProfile, useStorage } from 'utils/helpers';
import { STORAGE_TYPE } from 'utils/helpers/useStorage';

const initialState = {
  isLoading: true,
};

const TYPE = [
  'getProfile',
  'setProfile',
  'updateProfile',
  'removeProfile',
] as const;
interface DISPATCH_REDUCER {
  type: (typeof TYPE)[number];
  payload?: any;
}

const Reducer = (state: any, { type, payload }: DISPATCH_REDUCER) => {
  const { setItem, getItem, removeItem } = useStorage();
  let profile;
  let toReturn;

  let isLoading = true;

  if (type === 'getProfile') {
    profile = getItem(
      `${import.meta.env.VITE_APP_PREFIX}_profile`,
      STORAGE_TYPE.local,
    );

    if (!profile)
      profile = getItem(
        `${import.meta.env.VITE_APP_PREFIX}_profile`,
        STORAGE_TYPE.session,
      );

    if (profile) {
      profile = JSON.parse(profile);
    }

    toReturn = true;
    isLoading = false;
  }

  if (type === 'setProfile') {
    const storage = payload?.remember
      ? STORAGE_TYPE.local
      : STORAGE_TYPE.session;

    const {
      _token,
      _isEmailAddressVerified,
      _isOnboarded,
      _onboardingSteps,
      role,
      loginValidated,
    } = payload.profile;

    Cookies.set('_isEmailAddressVerified', _isEmailAddressVerified);
    Cookies.set('_isOnboarded', _isOnboarded);

    if (_onboardingSteps) {
      Cookies.set('_onboardingSteps', JSON.stringify(_onboardingSteps));
    }

    Cookies.set('_token', _token);

    if (loginValidated) {
      setItem(
        `${import.meta.env.VITE_APP_PREFIX}_profile`,
        JSON.stringify({ role }),
        storage,
      );
      profile = { role };
      toReturn = true;
    }

    isLoading = false;
  }

  if (type === 'updateProfile') {
    const {
      _token,
      _isEmailAddressVerified,
      _isOnboarded,
      _onboardingSteps,
      ...rest
    } = payload.profile;

    if (typeof _token !== 'undefined') Cookies.set('_token', _token);

    if (typeof _isEmailAddressVerified !== 'undefined') {
      Cookies.set('_isEmailAddressVerified', _isEmailAddressVerified);
    }

    if (typeof _isOnboarded !== 'undefined')
      Cookies.set('_isOnboarded', _isOnboarded);

    if (typeof _onboardingSteps !== 'undefined') {
      Cookies.set('_onboardingSteps', JSON.stringify(_onboardingSteps));
    }

    profile = {
      ...rest,
      ...state.profile,
    };

    let PROFILE = getItem(
      `${import.meta.env.VITE_APP_PREFIX}_profile`,
      STORAGE_TYPE.local,
    );
    let STORAGE = STORAGE_TYPE.local;

    if (!PROFILE) {
      PROFILE = getItem(
        `${import.meta.env.VITE_APP_PREFIX}_profile`,
        STORAGE_TYPE.session,
      );
      STORAGE = STORAGE_TYPE.session;
    }

    if (PROFILE) {
      PROFILE = JSON.parse(PROFILE);
    }

    setItem(
      `${import.meta.env.VITE_APP_PREFIX}_profile`,
      JSON.stringify({ ...PROFILE, ...rest }),
      STORAGE,
    );

    toReturn = true;
    isLoading = false;
  }

  if (type === 'removeProfile') {
    removeItem(
      `${import.meta.env.VITE_APP_PREFIX}_profile`,
      STORAGE_TYPE.session,
    );
    removeItem(
      `${import.meta.env.VITE_APP_PREFIX}_profile`,
      STORAGE_TYPE.local,
    );

    Cookies.remove('_isEmailAddressVerified');
    Cookies.remove('_isOnboarded');
    Cookies.remove('_token');

    profile = null;

    toReturn = true;
    isLoading = false;
  }

  if (toReturn) {
    return {
      profile,
      isLoading,
    };
  }

  return state;
};
export interface Profile {
  role: string;
  _id: string;
  accountId: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  fullName: string;
  photoUrl: string;
  phoneNumber: string;
  recoveryMobileNumber: string;
  passwordChangedAt: Date;
  isMfaEnabled: boolean;
  createdAt: Date;
}
type STATE = {
  profile: Profile;
  isLoading: boolean;
};
interface CONTEXT {
  state: STATE;
  dispatch: (payload: DISPATCH_REDUCER) => any;
}

export const ProfileContext = createContext({});
export const useProfileContext = () => useContext(ProfileContext) as CONTEXT;

const Profile: FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(Reducer, initialState);
  const { getItem } = useStorage();
  const { setProfile } = useProfile();
  const [misc, setMisc] = useState(null);

  useEffect(() => {
    dispatch({ type: 'getProfile' });
  }, []);

  useEffect(() => {
    window.addEventListener('storage', () => null);

    return () => window.removeEventListener('storage', () => null);
  }, [state]);

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  useEffect(() => {
    const storageMisc = getItem(
      `${import.meta.env.VITE_APP_PREFIX}_misc`,
      STORAGE_TYPE.local,
    );

    if (storageMisc) setMisc(JSON.parse(atob(storageMisc)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (misc) setProfile(misc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [misc]);

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
};

export default Profile;
